/**
 * React wrapper around the pure ``adaptiveFlow`` state machine plus
 * the axios side-effects that talk to the new SHSAT adaptive endpoints.
 *
 * The reducer comes from ``adaptiveFlow.ts`` unchanged — it is the
 * already-tested source of truth for status transitions. This hook
 * adds the missing piece: actually firing the HTTP calls that put
 * data into ``SLOT_FETCHED`` / ``SLOT_ADVANCED`` / ``ERROR`` events.
 *
 * Endpoints (defined in server/tests/views_shsat_adaptive.py):
 *
 *   GET  /tests/digital/shsat/adaptive/serve/<studentTestId>/<moduleId>/
 *        → { slotNumber, passageId, question, moduleComplete }
 *   POST /tests/digital/shsat/adaptive/save/<studentTestId>/<moduleId>/
 *        body: { slotNumber, questionId, answer, marked, timeSpent }
 *        → { saved, slotNumber, passageId, question, moduleComplete }
 *        (same SlotPayload shape; save returns the NEXT slot directly so
 *        a question transition is one HTTP round-trip, not two.)
 *
 * NOTE: the hook auto-fires START on mount. Consumers do not need to
 * call ``start()`` manually unless they're recovering from an error.
 * RETRY uses ``state.errorOrigin`` to decide whether to re-fetch or
 * re-submit the last answer; the re-submit path uses a ref to remember
 * what was last sent so the user does not have to re-enter their
 * answer after a network blip.
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';

import axios from 'config/axios';

import {
  AdaptiveFlowState,
  SlotPayload,
  canSubmit,
  hasError,
  initState,
  isComplete,
  isLoading,
  transition,
} from './adaptiveFlow';

export interface SubmitOptions {
  marked?: boolean;
  timeSpent?: number;
  // Passage highlights + answer-eliminator cross-outs, persisted server-side
  // exactly like the legacy flow (StudentPassageAnnotation /
  // StudentCrossedOutMultipleChoiceAnswer). ``passageId`` is the bare id
  // (no "passage " prefix); ``color`` is the label ("Blue"/"Pink").
  annotations?: { passageId: string; startIndex: number; endIndex: number; color: string | null }[];
  crossedOut?: number[];
}

export interface AdaptiveFlow {
  state: AdaptiveFlowState;
  /** Manually re-enter ``fetching``. Mount fires this automatically. */
  start: () => void;
  /** Submit the current slot's answer + advance. ``answer`` shape varies
   *  by question type (number for MC, list of indices for hot-text,
   *  list of [index, value] for fill-in-the-blank). */
  submit: (answer: unknown, options?: SubmitOptions) => void;
  /**
   * Persist the current answer WITHOUT advancing (advance=false), so the
   * end-of-passage interstitial can show before the passage locks. Stays
   * on the same slot; refreshes navigability for the Review list.
   */
  saveStay: (answer: unknown, options?: SubmitOptions) => void;
  /**
   * Best-effort, awaitable persist of the current answer WITHOUT advancing,
   * used to flush a selected-but-unsaved answer at timer expiry before the
   * whole-test auto-submit fires. Swallows errors and does not touch the
   * state machine, so the caller can race it against a short timeout.
   */
  flushCurrentAnswer: (answer: unknown, options?: SubmitOptions) => Promise<void>;
  /**
   * Advance past the just-finished passage (the interstitial's blue
   * forward arrow): serves the next slot, which locks the prior passage.
   */
  advanceNext: () => void;
  /** Re-try the last failed fetch or submit. Origin-aware. */
  retry: () => void;
  /**
   * Within-passage back navigation. Re-serves an already-answered slot
   * (with its prior answer) so the student can revise it. Server
   * rejects requests for slots in passages that have been exited.
   */
  goBack: (slotNumber: number) => void;

  // Memoized selectors mirroring the ones in ``adaptiveFlow.ts``.
  canSubmit: boolean;
  isLoading: boolean;
  isComplete: boolean;
  hasError: boolean;
}

interface UseAdaptiveFlowParams {
  studentTestId: string;
  moduleId: string;
}

interface SaveResponse {
  saved?: true;
  moduleComplete: boolean;
  slotNumber?: number;
  passageId?: string | null;
  sectionDirections?: string | null;
  question?: unknown;
  priorAnswer?: { answer: unknown } | null;
  navigability?: {
    canGoBack: boolean;
    prevSlotNumber: number | null;
    isLastInPassage: boolean;
    passageSlots?: { slotNumber: number; answered: boolean }[];
    passageSet?: {
      index: number | null;
      count: number;
      position: number;
      size: number;
    } | null;
    standaloneItems?: {
      index: number;
      count: number;
      position: number;
      size: number;
    } | null;
    passageGroupSize?: number | null;
  };
}

type ServeResponse = SaveResponse;

interface LastSubmit {
  answer: unknown;
  options: SubmitOptions;
}

export function useAdaptiveFlow(params: UseAdaptiveFlowParams): AdaptiveFlow {
  const { studentTestId, moduleId } = params;

  const [state, dispatch] = useReducer(transition, undefined, initState);

  // Remember the last submit so RETRY-after-submit-error can re-fire it
  // without the student re-entering their answer.
  const lastSubmitRef = useRef<LastSubmit | null>(null);

  // True when the in-flight save is a persist-only "stay" (end-of-passage
  // interstitial, advance=false) rather than a real advance. RETRY reads
  // this so a network blip during the interstitial save re-fires the
  // persist-only save instead of advancing (which would lock the passage).
  const pendingStayRef = useRef(false);

  // Track in-flight requests so unmount (real or Strict-Mode double)
  // can abort them BEFORE the response dispatches. A ref-only ``alive``
  // boolean is unsafe under React 18 Strict Mode because the second
  // mount re-runs the effect that resets the ref to true BEFORE the
  // first request resolves — the guard then fails open.
  // AbortController is the standard fix.
  const inflightRef = useRef<AbortController | null>(null);

  // Points at the latest ``fetchSlot`` so the request catch-blocks can
  // recover (re-serve the current slot) on a "passage is locked" 400
  // without taking a callback dependency on fetchSlot (avoids churn).
  const fetchSlotRef = useRef<((s?: number) => void) | null>(null);

  // Cancel any in-flight request on unmount so the dispatch never
  // fires against an unmounted reducer.
  useEffect(() => {
    return () => {
      inflightRef.current?.abort();
    };
  }, []);

  // -- side-effect helpers -------------------------------------------------

  const fetchSlot = useCallback(async (explicitSlot?: number) => {
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    try {
      const url = `tests/digital/shsat/adaptive/serve/${studentTestId}/${moduleId}/`;
      const config = {
        signal: controller.signal,
        ...(explicitSlot != null ? { params: { slot: explicitSlot } } : {}),
      };
      const response = await axios.get<ServeResponse>(url, config);
      if (controller.signal.aborted) return;
      const data = response.data;
      if (data.moduleComplete) {
        dispatch({ type: 'TEST_COMPLETE' });
        return;
      }
      if (data.slotNumber == null || data.question == null) {
        dispatch({ type: 'ERROR', message: 'Malformed slot payload.' });
        return;
      }
      dispatch({
        type: 'SLOT_FETCHED',
        slot: {
          slotNumber: data.slotNumber,
          passageId: data.passageId ?? null,
          sectionDirections: data.sectionDirections ?? null,
          question: data.question,
          priorAnswer: data.priorAnswer ?? null,
          navigability: data.navigability,
        },
      });
    } catch (err) {
      // Aborted by a newer request or by unmount — silently drop. The
      // controller's signal.aborted is set in both paths.
      if (controller.signal.aborted) return;
      // A back-nav re-serve (explicitSlot set) into a now-locked passage
      // means the passage already closed; recover by serving the current
      // forward slot instead of dead-ending on the error screen.
      if (isPassageLockedError(err) && explicitSlot != null) {
        dispatch({ type: 'START' });
        fetchSlotRef.current?.();
        return;
      }
      dispatch({ type: 'ERROR', message: extractErrorMessage(err) });
    }
  }, [moduleId, studentTestId]);
  fetchSlotRef.current = fetchSlot;

  const postSave = useCallback(
    async (slot: SlotPayload, answer: unknown, options: SubmitOptions) => {
      const questionId = (slot.question as { id?: number })?.id;
      if (typeof questionId !== 'number') {
        dispatch({ type: 'ERROR', message: 'Current slot has no questionId.' });
        return;
      }
      inflightRef.current?.abort();
      const controller = new AbortController();
      inflightRef.current = controller;
      try {
        const response = await axios.post<SaveResponse>(
          `tests/digital/shsat/adaptive/save/${studentTestId}/${moduleId}/`,
          {
            slotNumber: slot.slotNumber,
            questionId,
            answer,
            marked: options.marked ?? false,
            timeSpent: options.timeSpent ?? 0,
            annotations: options.annotations ?? [],
            crossedOut: options.crossedOut ?? [],
          },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const data = response.data;
        if (data.moduleComplete) {
          dispatch({ type: 'TEST_COMPLETE' });
          return;
        }
        if (data.slotNumber == null || data.question == null) {
          dispatch({ type: 'ERROR', message: 'Malformed slot payload.' });
          return;
        }
        dispatch({
          type: 'SLOT_ADVANCED',
          nextSlot: {
            slotNumber: data.slotNumber,
            passageId: data.passageId ?? null,
            sectionDirections: data.sectionDirections ?? null,
            question: data.question,
            priorAnswer: data.priorAnswer ?? null,
            navigability: data.navigability,
          },
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        // "Passage is locked" means this answer was already saved and we
        // advanced past it (a duplicate/late save). Recover silently by
        // re-serving the current slot rather than showing the fatal screen.
        if (isPassageLockedError(err)) {
          dispatch({ type: 'START' });
          fetchSlotRef.current?.();
          return;
        }
        dispatch({ type: 'ERROR', message: extractErrorMessage(err) });
      }
    },
    [moduleId, studentTestId],
  );

  // Persist-only save (advance=false): writes the current answer WITHOUT
  // serving the next slot, so the end-of-passage interstitial can show
  // before the passage locks. On success it stays on the SAME slot and
  // merely refreshes navigability (so the Review list reflects the
  // just-saved answer). Routed through submitting→ready (SLOT_ADVANCED
  // with the same slot) so it inherits the machine's error/retry path.
  const postSaveStay = useCallback(
    async (slot: SlotPayload, answer: unknown, options: SubmitOptions) => {
      const questionId = (slot.question as { id?: number })?.id;
      if (typeof questionId !== 'number') {
        dispatch({ type: 'ERROR', message: 'Current slot has no questionId.' });
        return;
      }
      inflightRef.current?.abort();
      const controller = new AbortController();
      inflightRef.current = controller;
      try {
        const response = await axios.post<SaveResponse>(
          `tests/digital/shsat/adaptive/save/${studentTestId}/${moduleId}/`,
          {
            slotNumber: slot.slotNumber,
            questionId,
            answer,
            marked: options.marked ?? false,
            timeSpent: options.timeSpent ?? 0,
            annotations: options.annotations ?? [],
            crossedOut: options.crossedOut ?? [],
            advance: false,
          },
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const data = response.data;
        dispatch({
          type: 'SLOT_ADVANCED',
          nextSlot: { ...slot, navigability: data.navigability ?? slot.navigability },
        });
      } catch (err) {
        if (controller.signal.aborted) return;
        // "Passage is locked" means this answer was already saved and we
        // advanced past it (a duplicate/late save). Recover silently by
        // re-serving the current slot rather than showing the fatal screen.
        if (isPassageLockedError(err)) {
          dispatch({ type: 'START' });
          fetchSlotRef.current?.();
          return;
        }
        dispatch({ type: 'ERROR', message: extractErrorMessage(err) });
      }
    },
    [moduleId, studentTestId],
  );

  // -- public actions ------------------------------------------------------

  const start = useCallback(() => {
    dispatch({ type: 'START' });
    fetchSlot();
  }, [fetchSlot]);

  const goBack = useCallback(
    (slotNumber: number) => {
      // Only meaningful from ``ready`` — disallow mid-submit.
      if (state.status !== 'ready') return;
      // Re-enter the fetching state so the loading affordance shows
      // while we re-serve the prior slot, then SLOT_FETCHED carries the
      // payload (including priorAnswer) into ``ready``.
      dispatch({ type: 'START' });
      fetchSlot(slotNumber);
    },
    [fetchSlot, state.status],
  );

  const submit = useCallback(
    (answer: unknown, options: SubmitOptions = {}) => {
      // Strict status guard: only ``ready`` accepts a submit. Without
      // this, a programmatic caller (or a child component that bypasses
      // ``canSubmit``) could double-fire while in ``submitting`` and
      // race two POSTs. The pure reducer rejects illegal SUBMIT_START
      // events, but the side effect fires OUTSIDE the reducer, so the
      // no-op transition doesn't protect us — we must guard here too.
      if (state.status !== 'ready') return;
      const slot = state.currentSlot;
      if (!slot) return;
      lastSubmitRef.current = { answer, options };
      pendingStayRef.current = false;
      dispatch({ type: 'SUBMIT_START' });
      postSave(slot, answer, options);
    },
    [postSave, state.currentSlot, state.status],
  );

  const saveStay = useCallback(
    (answer: unknown, options: SubmitOptions = {}) => {
      if (state.status !== 'ready') return;
      const slot = state.currentSlot;
      if (!slot) return;
      lastSubmitRef.current = { answer, options };
      pendingStayRef.current = true;
      dispatch({ type: 'SUBMIT_START' });
      postSaveStay(slot, answer, options);
    },
    [postSaveStay, state.currentSlot, state.status],
  );

  const advanceNext = useCallback(() => {
    // Blue forward arrow on the end-of-passage interstitial: serve the
    // next slot (the next passage's first question), which creates a
    // served row in a later group and thereby LOCKS the just-finished
    // passage. Preserve currentSlot for the loading overlay (same as
    // goBack), then SLOT_FETCHED carries the new slot into ``ready``.
    if (state.status !== 'ready') return;
    dispatch({ type: 'START' });
    fetchSlot();
  }, [fetchSlot, state.status]);

  const retry = useCallback(() => {
    // Read ``errorOrigin`` BEFORE dispatching RETRY: the reducer clears
    // it on the transition out.
    const origin = state.errorOrigin;
    const slotBeforeRetry = state.currentSlot;
    dispatch({ type: 'RETRY' });

    if (origin === 'submit') {
      const lastSubmit = lastSubmitRef.current;
      if (!slotBeforeRetry || !lastSubmit) {
        dispatch({
          type: 'ERROR',
          message: 'Cannot re-submit: no prior answer in memory.',
        });
        return;
      }
      // Re-fire the SAME kind of save that errored: a persist-only stay
      // must not turn into a real advance (which would lock the passage).
      if (pendingStayRef.current) {
        postSaveStay(slotBeforeRetry, lastSubmit.answer, lastSubmit.options);
      } else {
        postSave(slotBeforeRetry, lastSubmit.answer, lastSubmit.options);
      }
      return;
    }
    // 'fetch' or null (defensive): re-fetch.
    fetchSlot();
  }, [fetchSlot, postSave, postSaveStay, state.currentSlot, state.errorOrigin]);

  // Best-effort persist of the CURRENT slot's answer WITHOUT advancing.
  // Used to flush a selected-but-unsaved answer at timer expiry, before the
  // whole-test auto-submit fires — otherwise a buzzer-time selection on the
  // final (Math) question is lost, since the per-slot save normally only
  // fires on advance and the last slot has no advance. Awaitable and
  // error-swallowing so the caller can race it against a short timeout and
  // never block the mandatory auto-submit. Deliberately does NOT dispatch
  // into the state machine — we're unmounting into the submit gate next.
  const flushCurrentAnswer = useCallback(
    async (answer: unknown, options: SubmitOptions = {}): Promise<void> => {
      if (state.status !== 'ready') return;
      const slot = state.currentSlot;
      if (!slot) return;
      const questionId = (slot.question as { id?: number })?.id;
      if (typeof questionId !== 'number') return;
      try {
        await axios.post(
          `tests/digital/shsat/adaptive/save/${studentTestId}/${moduleId}/`,
          {
            slotNumber: slot.slotNumber,
            questionId,
            answer,
            marked: options.marked ?? false,
            timeSpent: options.timeSpent ?? 0,
            annotations: options.annotations ?? [],
            crossedOut: options.crossedOut ?? [],
            advance: false,
          },
        );
      } catch {
        // Swallow: a failed/late buzzer flush must never block auto-submit.
      }
    },
    [studentTestId, moduleId, state.currentSlot, state.status],
  );

  // -- auto-start on mount -------------------------------------------------

  // Fire START + initial fetch once per (studentTestId, moduleId).
  // ``start`` is intentionally excluded from the deps array: its
  // identity changes when ``state.currentSlot`` changes (via useCallback
  // closure), and re-running this effect on every answer would re-fire
  // the GET. We want mount-once semantics.
  useEffect(() => {
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-once
  }, [studentTestId, moduleId]);

  return {
    state,
    start,
    submit,
    saveStay,
    flushCurrentAnswer,
    advanceNext,
    retry,
    goBack,
    canSubmit: canSubmit(state),
    isLoading: isLoading(state),
    isComplete: isComplete(state),
    hasError: hasError(state),
  };
}


// True when the server rejected a save/serve because the slot's passage is
// already locked (saved-then-advanced). Treated as recoverable, not fatal.
function isPassageLockedError(err: unknown): boolean {
  const msg = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message ?? '';
  return /passage is locked/i.test(msg);
}

function extractErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
    return (
      anyErr.response?.data?.message ??
      anyErr.message ??
      'Network error.'
    );
  }
  return 'Network error.';
}
